import { useEffect, type ReactNode } from 'react';
import { Accessibility, ArrowLeft } from 'lucide-react';
import { Link } from 'wouter';

interface PublicPolicyPageProps {
  title: string;
  summary: string;
  testId: string;
  children: ReactNode;
  statusLabel?: string;
  dateLabel?: string;
  reviewNotice?: ReactNode;
}

export function PublicPolicyPage({
  title,
  summary,
  testId,
  children,
  statusLabel,
  dateLabel = 'Effective date: September 2, 2026',
  reviewNotice,
}: PublicPolicyPageProps) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | ADA Widget`;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  return (
    <div className="min-h-[100dvh] bg-[#102645] text-slate-300 font-sans selection:bg-[#5687FF] selection:text-slate-950">
      <a
        href="#policy-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-[#22C55E] focus:px-4 focus:py-2 focus:font-bold focus:text-slate-950"
      >
        Skip to policy
      </a>

      <header className="sticky top-0 z-50 border-b border-[#21477D]/60 bg-[#102645]/95 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6 md:h-20">
          <Link
            href="/"
            data-testid="link-policy-logo-home"
            className="flex items-center gap-2 text-lg font-bold tracking-tight text-white md:text-xl"
            aria-label="ADA Widget home"
          >
            <span aria-hidden="true" className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#3062A5] bg-[#16335C] text-[#5687FF]">
              <Accessibility className="h-5 w-5" />
            </span>
            ADA Widget
          </Link>
          <Link
            href="/"
            data-testid="link-policy-back-home"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#8FA8FF] transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to home
          </Link>
        </div>
      </header>

      <main id="policy-content" tabIndex={-1} className="px-4 py-12 focus:outline-none sm:px-6 md:py-20">
        <article className="mx-auto max-w-3xl">
          <div className="mb-10 border-b border-[#21477D] pb-10">
            {statusLabel && (
              <div className="mb-5 inline-flex items-center rounded-full border border-[#FBBF24]/30 bg-[#FBBF24]/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#FCD34D]">
                {statusLabel}
              </div>
            )}
            <h1 data-testid={testId} className="text-3xl font-extrabold tracking-tight text-white md:text-5xl">
              {title}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#AAB8CB] md:text-lg">
              {summary}
            </p>
            <p className="mt-4 text-sm font-semibold text-slate-400">{dateLabel}</p>
          </div>

          <div className="policy-copy space-y-9 text-[15px] leading-7 text-slate-300">
            {children}
          </div>

          {reviewNotice && (
            <aside className="mt-12 rounded-xl border border-[#3062A5]/70 bg-[#16335C] p-5 text-sm leading-relaxed text-slate-300">
              {reviewNotice}
            </aside>
          )}

          <nav aria-label="Other policies" className="mt-10 flex flex-wrap gap-x-5 gap-y-3 border-t border-[#21477D] pt-8 text-sm font-semibold">
            <Link href="/terms" data-testid="link-policy-terms" className="text-[#8FA8FF] hover:text-white">Terms of Service</Link>
            <Link href="/privacy" data-testid="link-policy-privacy" className="text-[#8FA8FF] hover:text-white">Privacy Policy</Link>
            <Link href="/refund" data-testid="link-policy-refund" className="text-[#8FA8FF] hover:text-white">Refund / Cancellation</Link>
          </nav>
        </article>
      </main>
    </div>
  );
}